const directionKeys = {
    ltr: {
        previous: 'ArrowLeft',
        next: 'ArrowRight',
    },
    rtl: {
        previous: 'ArrowRight',
        next: 'ArrowLeft',
    },
};
function getNextElementIndex(args) {
    const { userAction, activeElementIndex, managedElements, cycle } = args;
    switch (userAction) {
        case 'firstItem': {
            return 0;
        }
        case 'previousItem': {
            const newIndex = activeElementIndex - 1;
            if (cycle) {
                return newIndex < 0 ? managedElements.length - 1 : newIndex;
            }
            return Math.max(0, newIndex);
        }
        case 'nextItem': {
            const newIndex = activeElementIndex + 1;
            if (cycle) {
                return newIndex > managedElements.length - 1 ? 0 : newIndex;
            }
            return Math.min(managedElements.length - 1, newIndex);
        }
        case 'lastItem': {
            return managedElements.length - 1;
        }
        default:
            throw new Error();
    }
}
const DEFAULT_ARGS = {
    cycle: false,
};
/**
 * Creates utilities for managing keyboard focus on a collection of elements.
 *
 * ---
 *
 * Supported navigation keys:
 * - `ArrowLeft | ArrowRight | Home | End`.
 *
 * ---
 *
 * Hints:
 * - `ltr | rtl` css directions are taken into account.
 * - Included elements must be focussable (e.g. input elements).
 *   If they're not focussable by default you have to set `tabIndex >= 0`.
 * - As is best practice with item collections and react:
 *   keeping your element keys stable will ensure that element focus is not lost during re-renders.
 *   To ensure good UX it's recommended to follow this practice.
 *   Especially when asynchonous processes can lead to the re-ordering of included elements.
 *
 * ---
 *
 * *Disclaimer: At the time of writing only horizontal navigation is supported.
 * For "vertical" or "mixed (grid)" navigation this hook would need to be extended.*
 *
 * ---
 *
 * Usage
 *
 * ```
 * const { handleKeyboardNavigation, includeInKeyboardNavigation } = useKeyboardNavigation()
 *
 * return (
 *  <ul onKeyDown={handleKeyboardNavigation}>
 *    {items.map((item) => (
 *      <li>
 *        <button ref={includeInKeyboardNavigation}>
 *          {item}
 *        </button>
 *      </li>
 *    )}
 *  </ul>
 * )
 * ```
 */
export function useKeyboardNavigation(args = DEFAULT_ARGS) {
    const managedElements = [];
    const includeInKeyboardNavigation = (element) => {
        if (!element) {
            return;
        }
        managedElements.push(element);
    };
    const handleKeyboardNavigation = (event) => {
        if (!document.activeElement) {
            return;
        }
        const activeElementIndex = managedElements.indexOf(document.activeElement);
        if (activeElementIndex > -1) {
            let nextElementIndex = activeElementIndex;
            const direction = getComputedStyle(event.currentTarget).direction === 'rtl'
                ? 'rtl'
                : 'ltr';
            switch (event.key) {
                case directionKeys[direction].previous: {
                    event.preventDefault();
                    nextElementIndex = getNextElementIndex({
                        userAction: 'previousItem',
                        activeElementIndex,
                        managedElements,
                        cycle: args.cycle,
                    });
                    break;
                }
                case directionKeys[direction].next: {
                    event.preventDefault();
                    nextElementIndex = getNextElementIndex({
                        userAction: 'nextItem',
                        activeElementIndex,
                        managedElements,
                        cycle: args.cycle,
                    });
                    break;
                }
                case 'Home': {
                    event.preventDefault();
                    nextElementIndex = getNextElementIndex({
                        userAction: 'firstItem',
                        activeElementIndex,
                        managedElements,
                        cycle: args.cycle,
                    });
                    break;
                }
                case 'End': {
                    event.preventDefault();
                    nextElementIndex = getNextElementIndex({
                        userAction: 'lastItem',
                        activeElementIndex,
                        managedElements,
                        cycle: args.cycle,
                    });
                    break;
                }
            }
            if (nextElementIndex !== activeElementIndex) {
                managedElements[nextElementIndex].focus();
            }
        }
    };
    return {
        handleKeyboardNavigation,
        includeInKeyboardNavigation,
    };
}
//# sourceMappingURL=useKeyboardNavigation.mjs.mjs.map